import { useEffect, useState } from "react";
import { Bookmark, Play, Trash2, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { fmtUSDateTime } from "../lib/date";

/**
 * Reusable Saved-Searches strip mounted inside LoopNet/Crexi pull dialogs.
 *
 * Props:
 *   source         "loopnet" | "crexi"
 *   currentParams  the live form-state params dict (used by Save)
 *   canSave        boolean — disables Save when params aren't valid yet
 *   onApply        (params) => void — re-hydrate parent form from a saved row
 *   onAfterRun     (data) => Promise — parent refreshes its staged list /
 *                  starts polling. For loopnet receives {items, count};
 *                  for crexi receives {run_id, status}.
 *
 * Endpoints used:
 *   GET    /api/saved-searches?source=
 *   POST   /api/saved-searches
 *   DELETE /api/saved-searches/{id}
 *   POST   /api/saved-searches/{id}/run
 */
export default function SavedSearchesBar({ source, currentParams, canSave, onApply, onAfterRun }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [runningId, setRunningId] = useState(null);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [name, setName] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/saved-searches?source=${encodeURIComponent(source)}`);
      setItems(r.data?.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [source]);

  const save = async (e) => {
    e?.preventDefault();
    const trimmed = (name || "").trim();
    if (!trimmed) {
      toast.error("Name your saved search");
      return;
    }
    setSavingId("__new__");
    try {
      await api.post("/saved-searches", { name: trimmed, source, params: currentParams });
      toast.success(`Saved "${trimmed}"`);
      setName("");
      setShowSaveForm(false);
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save");
    } finally {
      setSavingId(null);
    }
  };

  const run = async (item) => {
    setRunningId(item.id);
    try {
      const r = await api.post(`/saved-searches/${item.id}/run`);
      await refresh();
      await onAfterRun?.(r.data);
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiError(err.response?.data?.detail);
      if (status === 429) toast.error(detail || "Rate limited — try again in a minute");
      else if (status === 503) toast.error(detail || "Provider not configured");
      else toast.error(detail || "Run failed");
    } finally {
      setRunningId(null);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete saved search "${item.name}"?`)) return;
    try {
      await api.delete(`/saved-searches/${item.id}`);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      toast.success("Deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not delete");
    }
  };

  const summarize = (params) => {
    if (source === "loopnet") {
      const bits = [params?.city, params?.sale_or_lease === "lease" ? "Lease" : "Sale"];
      if (params?.property_type) bits.push(params.property_type);
      if (params?.limit) bits.push(`${params.limit} max`);
      return bits.filter(Boolean).join(" · ");
    }
    if (source === "crexi") {
      const s = (params?.states || []).join(", ");
      const t = (params?.property_types || []).join(", ");
      const m = params?.max_properties ? `${params.max_properties} max` : "";
      return [s, t, m].filter(Boolean).join(" · ");
    }
    return "";
  };

  return (
    <div className="border border-slate-200 bg-slate-50/50 rounded-sm p-3" data-testid={`saved-searches-${source}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 overline text-slate-700">
          <Bookmark className="w-3.5 h-3.5" />
          Saved searches {items.length > 0 && <span className="text-slate-900">({items.length})</span>}
        </div>
        <button
          type="button"
          onClick={() => setShowSaveForm((v) => !v)}
          disabled={!canSave}
          className="text-xs font-mono uppercase tracking-widest text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`saved-searches-${source}-toggle-save`}
          title={canSave ? "Save current search" : "Fill in the form first"}
        >
          <Plus className="w-3.5 h-3.5" /> Save current
        </button>
      </div>

      {showSaveForm && (
        <form onSubmit={save} className="flex items-center gap-2 mb-2" data-testid={`saved-searches-${source}-save-form`}>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Charlotte Office, sale"
            className="input-clean flex-1 h-8 text-sm"
            maxLength={80}
            data-testid={`saved-searches-${source}-name-input`}
          />
          <button
            type="submit"
            disabled={savingId === "__new__"}
            className="btn-primary-dark h-8 px-3 text-xs inline-flex items-center gap-1 disabled:opacity-60"
            data-testid={`saved-searches-${source}-save-btn`}
          >
            {savingId === "__new__" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bookmark className="w-3 h-3" />}
            Save
          </button>
          <button
            type="button"
            onClick={() => { setShowSaveForm(false); setName(""); }}
            className="text-xs text-slate-500 hover:text-slate-800 px-2"
          >
            Cancel
          </button>
        </form>
      )}

      {loading ? (
        <div className="text-xs text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-slate-500">No saved searches yet — fill in the form above and click <b>Save current</b>.</div>
      ) : (
        <ul className="divide-y divide-slate-200" data-testid={`saved-searches-${source}-list`}>
          {items.map((it) => (
            <li key={it.id} className="py-1.5 flex items-center gap-2">
              <button
                type="button"
                onClick={() => onApply?.(it.params || {})}
                className="text-left flex-1 min-w-0 hover:text-emerald-700 group"
                title="Click to load these params into the form"
                data-testid={`saved-searches-${source}-apply-${it.id}`}
              >
                <div className="text-sm font-medium text-slate-900 truncate group-hover:underline">{it.name}</div>
                <div className="text-[11px] text-slate-500 truncate font-mono">
                  {summarize(it.params)}
                  {it.last_run_at && (
                    <span className="ml-2 text-slate-400">
                      · last run {fmtUSDateTime(it.last_run_at)}
                      {typeof it.last_result_count === "number" && ` · ${it.last_result_count} staged`}
                    </span>
                  )}
                </div>
              </button>
              <button
                type="button"
                disabled={runningId === it.id}
                onClick={() => run(it)}
                className="text-xs text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 px-2 py-1 disabled:opacity-60"
                data-testid={`saved-searches-${source}-run-${it.id}`}
              >
                {runningId === it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                Run
              </button>
              <button
                type="button"
                onClick={() => remove(it)}
                className="text-slate-400 hover:text-red-600 p-1"
                title="Delete"
                data-testid={`saved-searches-${source}-delete-${it.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
